import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiScope, getMsal } from './msal';

// Single in-flight token acquisition. A view typically fires several /api/admin/* calls
// in parallel on mount (each Pinia store's load()). Without this guard the first one
// triggers the interactive fallback and every other concurrent caller throws
// `interaction_in_progress` (MSAL only permits one interaction at a time). Collapsing
// them onto one shared promise removes that race entirely. The promise is nulled on
// settle so the NEXT burst re-acquires (acquireTokenSilent is itself cache-cheap).
let inFlight: Promise<string> | null = null;

async function acquireToken(): Promise<string> {
  const msal = getMsal();
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (!account) throw new Error('Not signed in');

  try {
    const result = await msal.acquireTokenSilent({ account, scopes: [apiScope()] });
    return result.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      // Full-page redirect rather than popup. After inactivity the silent refresh
      // fails from a background fetch (no user gesture) — a popup launched from
      // that context is blocked by the browser and/or collides with any other
      // pending interaction. A redirect is gesture-independent; the page navigates
      // to AAD and back, and handleRedirectPromise() in initialiseMsal() resumes
      // the session on return (navigateToLoginRequestUrl restores the route).
      await msal.acquireTokenRedirect({ account, scopes: [apiScope()] });
      // acquireTokenRedirect navigates away — control never returns here. The
      // never-resolving promise just satisfies the return type while the browser
      // unloads.
      return new Promise<string>(() => { /* unreachable: page is navigating */ });
    }
    throw e;
  }
}

// Wrap fetch so every /api/admin/* call gets a fresh AAD bearer. Silent refresh first;
// full-page redirect to AAD when interaction is genuinely required.
export async function authHeaders(): Promise<Record<string, string>> {
  inFlight ??= acquireToken().finally(() => { inFlight = null; });
  const token = await inFlight;
  return { Authorization: `Bearer ${token}` };
}
